import { useState, useEffect } from "react";
import { DoorClosed, Calculator } from "lucide-react";
import { CalculatorDialog } from "@/components/ui/calculator-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CashCountForm } from "./CashCountForm";
import { OpenConsumptionWarningDialog } from "./OpenConsumptionWarningDialog";
import { useCashCount } from "@/hooks/useCashCount";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";

interface CloseCashRegisterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (amount: number, notes?: string, cashRegisterId?: string) => Promise<void>;
  totals: {
    openingAmount: number;
    supplies: number;
    withdrawals: number;
  };
  cashRegisterId?: string;
  openTablesCount?: number;
  openCommandsCount?: number;
  totalOpenAmount?: number;
  openTableNumbers?: string[];
  openCommandNumbers?: string[];
  staleTablesCount?: number;
  staleCommandsCount?: number;
  staleAmount?: number;
  staleTableNumbers?: string[];
  staleCommandNumbers?: string[];
}

export function CloseCashRegisterDialog({
  open,
  onOpenChange,
  onConfirm,
  totals,
  cashRegisterId,
  openTablesCount = 0,
  openCommandsCount = 0,
  totalOpenAmount = 0,
  openTableNumbers = [],
  openCommandNumbers = [],
  staleTablesCount = 0,
  staleCommandsCount = 0,
  staleAmount = 0,
  staleTableNumbers = [],
  staleCommandNumbers = [],
}: CloseCashRegisterDialogProps) {
  const [closingAmount, setClosingAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countMode, setCountMode] = useState<"simple" | "detailed">("simple");
  const [showWarning, setShowWarning] = useState(false);

  const {
    paymentMethodCounts,
    updateQuantity,
    updatePaymentMethodAmount,
    resetCount,
    getTotal,
    getPaymentMethodsTotal,
    getNotes,
    getCoins,
    saveCashCount,
  } = useCashCount();

  const { getActivePaymentMethods } = usePaymentMethods();

  // Reset quando o dialog abrir
  useEffect(() => {
    if (open) {
      setClosingAmount("");
      setNotes("");
      setCountMode("simple");
      setShowWarning(false);
      resetCount();
    }
  }, [open, resetCount]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const hasOpenConsumption = openTablesCount > 0 || openCommandsCount > 0;

  const handleCloseClick = () => {
    if (hasOpenConsumption) {
      setShowWarning(true);
    } else {
      handleConfirm();
    }
  };

  const handleConfirm = async () => {
    setShowWarning(false);
    setIsSubmitting(true);
    try {
      const cashAmount = countMode === "detailed" ? getTotal() : parseFloat(closingAmount) || 0;
      const paymentMethodsAmount = countMode === "detailed" ? getPaymentMethodsTotal() : 0;
      const totalAmount = cashAmount + paymentMethodsAmount;
      
      // Save detailed count if using detailed mode
      if (countMode === "detailed" && cashRegisterId) {
        await saveCashCount(cashRegisterId);
      }

      await onConfirm(totalAmount, notes || undefined, cashRegisterId);
      setClosingAmount("");
      setNotes("");
      resetCount();
    } finally {
      setIsSubmitting(false);
    }
  };

  const baseExpected = totals.openingAmount + totals.supplies - totals.withdrawals;
  const activePaymentMethods = getActivePaymentMethods();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DoorClosed className="h-5 w-5" />
              Fechar Caixa
              <div className="ml-auto">
                <CalculatorDialog />
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Summary */}
            <div className="rounded-lg border p-4 space-y-2">
              <h4 className="font-medium text-sm text-muted-foreground">Resumo do Caixa</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Fundo Inicial:</span>
                <span className="text-right font-medium">{formatCurrency(totals.openingAmount)}</span>
                
                <span className="text-muted-foreground">Suprimentos:</span>
                <span className="text-right font-medium text-green-600">+{formatCurrency(totals.supplies)}</span>
                
                <span className="text-muted-foreground">Sangrias:</span>
                <span className="text-right font-medium text-red-600">-{formatCurrency(totals.withdrawals)}</span>
                
                <span className="text-muted-foreground font-medium border-t pt-2">Base Esperada:</span>
                <span className="text-right font-bold border-t pt-2">{formatCurrency(baseExpected)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                * O valor das vendas em dinheiro será adicionado automaticamente ao cálculo final.
              </p>
            </div>

            {/* Counting Mode Tabs */}
            <Tabs value={countMode} onValueChange={(v) => setCountMode(v as "simple" | "detailed")}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="simple">Valor Simples</TabsTrigger>
                <TabsTrigger value="detailed" className="gap-2">
                  <Calculator className="h-4 w-4" />
                  Contagem Detalhada
                </TabsTrigger>
              </TabsList>

              <TabsContent value="simple" className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="closingAmount">Valor Total Contado no Caixa *</Label>
                  <Input
                    id="closingAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="R$ 0,00"
                    value={closingAmount}
                    onChange={(e) => setClosingAmount(e.target.value)}
                    autoFocus
                  />
                  <p className="text-sm text-muted-foreground">
                    Some todo o dinheiro em espécie e comprovantes de pagamento (cartões, PIX, vouchers, etc.) e informe o valor total.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="detailed" className="mt-4">
                <CashCountForm
                  notes={getNotes()}
                  coins={getCoins()}
                  total={getTotal()}
                  onUpdateQuantity={updateQuantity}
                  paymentMethods={activePaymentMethods}
                  paymentMethodCounts={paymentMethodCounts}
                  onUpdatePaymentMethodAmount={updatePaymentMethodAmount}
                />
              </TabsContent>
            </Tabs>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Observações (opcional)</Label>
              <Textarea
                id="notes"
                placeholder="Anotações sobre o fechamento..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCloseClick} 
              disabled={isSubmitting || (countMode === "simple" && !closingAmount)}
              variant="destructive"
            >
              {isSubmitting ? "Fechando..." : "Fechar Caixa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OpenConsumptionWarningDialog
        open={showWarning}
        onOpenChange={setShowWarning}
        onConfirm={handleConfirm}
        openTablesCount={openTablesCount}
        openCommandsCount={openCommandsCount}
        totalOpenAmount={totalOpenAmount}
        openTableNumbers={openTableNumbers}
        openCommandNumbers={openCommandNumbers}
        staleTablesCount={staleTablesCount}
        staleCommandsCount={staleCommandsCount}
        staleAmount={staleAmount}
        staleTableNumbers={staleTableNumbers}
        staleCommandNumbers={staleCommandNumbers}
      />
    </>
  );
}
